<template>
    <div
        ref="container"
        class="ActionReviewDocuments flex flex-col"
        :class="{ 'ActionReviewDocuments--fixed': isFixedMode }"
    >
        <div class="ActionReviewDocuments__header">
            <div class="ActionReviewDocuments__headerTitle">
                <EidIcon :icon-src="iconDocument" class="w-[18px] h-[18px] text-eidDarkBlue" />Documents
            </div>
            <div
                v-if="documents.length > 1"
                class="ActionReviewDocuments__pagination"
            >
                <span class="ActionReviewDocuments__paginationText">
                    {{ currentIndex + 1 }} of {{ documents.length }} documents
                </span>
                <EidIcon
                    :icon-src="iconChevronLeft"
                    class="ActionReviewDocuments__paginationArrow"
                    :class="{ 'ActionReviewDocuments__paginationArrow--disabled': currentIndex === 0 }"
                    @click="previousDoc"
                />
                <EidIcon
                    :icon-src="iconChevronRight"
                    class="ActionReviewDocuments__paginationArrow"
                    :class="{ 'ActionReviewDocuments__paginationArrow--disabled': currentIndex === documents.length - 1 }"
                    @click="nextDoc"
                />
            </div>
        </div>
        <div class="ActionReviewDocuments__content flex-1">
            <div
                v-if="documents.length === 0"
                class="ActionReviewDocuments__empty"
            >
                <div class="ActionReviewDocuments__emptyContent">
                    <div class="ActionReviewDocuments__emptyContentIcon">
                        <EidIcon :icon-src="iconDocument" class="w-[18px] h-[18px] text-eidTrustGreen" />
                    </div>
                    <div class="text-eidDarkBlue">
                        No Documents Available
                    </div>
                    <div class="text-eidTechGray text-sm">
                        There are no documents associated with this record.
                    </div>
                </div>
            </div>
            <BinaryDataDisplay
                v-else-if="currentContent"
                class="ActionReviewDocuments__display"
                :value="{
                    data: currentContent.data,
                    mimeType: currentContent.mimeType,
                }"
            />
        </div>
    </div>
</template>

<style lang="scss">
.ActionReviewDocuments {
    min-height: 0;

    &--fixed {
        position: fixed;
        left: var(--documents-left);
        width: var(--documents-width);
        top: var(--documents-top);
        height: var(--documents-height);
        will-change: top, height;
    }

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: var(--eidWhite);
        border-top-left-radius: 8.75px;
        border-top-right-radius: 8.75px;
        padding: 21px;
        border: 1px solid var(--eidSecureBlue-10);
    }

    &__headerTitle {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 16px;
        font-weight: 500;
        color: var(--eidDarkBlue);
    }

    &__pagination {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    &__paginationText {
        font-size: 14px;
        color: var(--eidDarkBlue);
    }

    &__paginationArrow {
        width: 16px;
        height: 16px;
        cursor: pointer;

        &:hover:not(&--disabled) {
            opacity: 0.7;
        }

        &--disabled {
            opacity: 0.3;
            cursor: not-allowed;
        }
    }

    &__content {
        background: var(--eidWhite);
        border-left: 1px solid var(--eidSecureBlue-10);
        border-right: 1px solid var(--eidSecureBlue-10);
        border-bottom: 1px solid var(--eidSecureBlue-10);
        border-bottom-left-radius: 8.75px;
        border-bottom-right-radius: 8.75px;
        overflow: hidden;
        min-height: 0;
    }

    &__empty, &__display {
        height: 100%;
    }

    &__empty {
        padding: 21px;
    }

    &__emptyContent {
        display: flex;
        padding: 28px;
        background: var(--eidBgGrayTones-lightest);
        border: 2px solid var(--eidSecureBlue-10);
        border-radius: 8.75px;
        border-style: dashed;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 12px;
    }

    &__emptyContentIcon {
        width: 48px;
        height: 48px;
        background-color: var(--eidTrustGreen-10);
        border-radius: 8.75px;
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
            color: var(--eidTrustGreen);
        }
    }

    &__display {
        .BinaryDataDisplay__frame {
            height: 100%;
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faAngleLeft, faAngleRight } from '@fortawesome/pro-light-svg-icons';
    import { faFileText } from '@fortawesome/pro-regular-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { DocumentDataReviewDocument } from '@evidentid/tprm-portal-lib/models/document-data-review';

    import BinaryDataDisplay from '@/components/binary-data-display/BinaryDataDisplay.vue';

    export default defineComponent({
        name: 'ActionReviewDocuments',
        components: {
            BinaryDataDisplay,
            EidIcon,
        },
        props: {
            documents: {
                type: Array as PropType<DocumentDataReviewDocument[]>,
                default: (): DocumentDataReviewDocument[] => [] as DocumentDataReviewDocument[],
            },
            storageId: {
                type: String as PropType<string>,
                default: '',
            },
            fixedWidth: {
                type: Number as PropType<number>,
                default: 0,
            },
            fixedTop: {
                type: Number as PropType<number>,
                default: 0,
            },
            fixedLeft: {
                type: Number as PropType<number>,
                default: 0,
            },
            fixedBottomOffset: {
                type: Number as PropType<number>,
                default: 21,
            },
        },
        data() {
            return {
                currentIndex: 0,
                iconChevronLeft: faAngleLeft,
                iconChevronRight: faAngleRight,
                iconDocument: faFileText,
            };
        },
        computed: {
            currentContent(): DocumentDataReviewDocument | null {
                return (this as any).documents[(this as any).currentIndex] || null;
            },
            storageKey(): string {
                return (this as any).storageId ? `action-review-document-index-${(this as any).storageId}` : '';
            },
            isFixedMode(): boolean {
                return (this as any).fixedWidth > 0;
            },
        },
        watch: {
            documents: {
                handler() {
                    const storageKey = (this as any).storageKey;

                    if (storageKey && (this as any).documents.length > 0) {
                        const savedIndex = localStorage.getItem(storageKey);
                        if (savedIndex !== null) {
                            const index = parseInt(savedIndex, 10);
                            if (!isNaN(index) && index >= 0 && index < (this as any).documents.length) {
                                (this as any).currentIndex = index;
                                return;
                            }
                        }
                    }

                    (this as any).currentIndex = 0;
                },
                immediate: true,
            },
            currentIndex(newIndex: number) {
                const storageKey = (this as any).storageKey;
                if (storageKey) {
                    localStorage.setItem(storageKey, newIndex.toString());
                }
            },
            fixedWidth: {
                handler(width: number) {
                    this.updateCssProperty('--documents-width', `${width}px`);
                },
                immediate: true,
            },
            fixedTop: {
                handler(top: number) {
                    this.updateCssProperty('--documents-top', `${top}px`);
                    this.updateCssProperty(
                        '--documents-height',
                        `calc(100vh - ${top}px - ${(this as any).fixedBottomOffset}px)`,
                    );
                },
                immediate: true,
            },
            fixedLeft: {
                handler(left: number) {
                    this.updateCssProperty('--documents-left', `${left}px`);
                },
                immediate: true,
            },
            fixedBottomOffset: {
                handler(bottomOffset: number) {
                    this.updateCssProperty(
                        '--documents-height',
                        `calc(100vh - ${(this as any).fixedTop}px - ${bottomOffset}px)`,
                    );
                },
                immediate: true,
            },
        },
        mounted() {
            this.$nextTick(() => {
                this.syncCssProperties();
            });
        },
        methods: {
            nextDoc(): void {
                if ((this as any).currentIndex < (this as any).documents.length - 1) {
                    (this as any).currentIndex += 1;
                }
            },
            previousDoc(): void {
                if ((this as any).currentIndex > 0) {
                    (this as any).currentIndex -= 1;
                }
            },
            updateCssProperty(property: string, value: string): void {
                const container = this.$refs.container as HTMLElement | undefined;
                if (container) {
                    container.style.setProperty(property, value);
                }
            },
            syncCssProperties(): void {
                this.updateCssProperty('--documents-width', `${(this as any).fixedWidth}px`);
                this.updateCssProperty('--documents-top', `${(this as any).fixedTop}px`);
                this.updateCssProperty('--documents-left', `${(this as any).fixedLeft}px`);
                this.updateCssProperty(
                    '--documents-height',
                    `calc(100vh - ${(this as any).fixedTop}px - ${(this as any).fixedBottomOffset}px)`,
                );
            },
        },
    });
</script>
